import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, Tooltip, CircularProgress, Alert, Snackbar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Stack, Select, FormControl, InputLabel,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import { adminApi } from '../../api/adminApi';

const ROLE_OPTIONS = [
  { value: 'platform_admin', label: 'Admin', color: 'primary' },
  { value: 'platform_manager', label: 'Manager', color: 'warning' },
];

const ROLE_COLOR = {
  platform_admin: 'primary',
  platform_superadmin: 'error',
  platform_manager: 'warning',
};

const ROLE_LABEL = {
  platform_admin: 'Admin',
  platform_superadmin: 'Super Admin',
  platform_manager: 'Manager',
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', role: 'platform_admin' });
  const [creating, setCreating] = useState(false);

  // Role edit dialog
  const [roleDialog, setRoleDialog] = useState({ open: false, userId: '', currentRole: '' });
  const [newRole, setNewRole] = useState('');
  const [updatingRole, setUpdatingRole] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.listAdminUsers();
      setUsers(data.users || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!createForm.email) { return; }
    setCreating(true);
    try {
      await adminApi.createAdminUser(createForm.email, createForm.role);
      setToast({ open: true, message: 'User created. A temporary password has been emailed.', severity: 'success' });
      setCreateOpen(false);
      setCreateForm({ email: '', role: 'platform_admin' });
      load();
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleToggleEnabled = async (userId, enabled) => {
    try {
      if (enabled) {
        await adminApi.disableAdminUser(userId);
        setToast({ open: true, message: 'User disabled.', severity: 'info' });
      } else {
        await adminApi.enableAdminUser(userId);
        setToast({ open: true, message: 'User enabled.', severity: 'success' });
      }
      load();
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleRoleUpdate = async () => {
    setUpdatingRole(true);
    try {
      await adminApi.updateAdminUserRole(roleDialog.userId, newRole);
      setToast({ open: true, message: 'Role updated.', severity: 'success' });
      setRoleDialog({ open: false, userId: '', currentRole: '' });
      load();
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setUpdatingRole(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Platform Users</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Add User
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No platform users found.
                </TableCell>
              </TableRow>
            ) : users.map((u) => (
              <TableRow key={u.userId} hover>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip
                    label={ROLE_LABEL[u.role] || u.role}
                    size="small"
                    color={ROLE_COLOR[u.role] || 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.enabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    color={u.enabled ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell align="right">
                  {u.role !== 'platform_superadmin' && (
                    <>
                      <Tooltip title="Change role">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setRoleDialog({ open: true, userId: u.userId, currentRole: u.role });
                            setNewRole(u.role);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={u.enabled ? 'Disable user' : 'Enable user'}>
                        <IconButton size="small" onClick={() => handleToggleEnabled(u.userId, u.enabled)}>
                          {u.enabled
                            ? <BlockIcon fontSize="small" color="error" />
                            : <CheckCircleIcon fontSize="small" color="success" />}
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Platform User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              size="small"
              value={createForm.email}
              onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              autoFocus
            />
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={createForm.role}
                onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              A temporary password will be emailed to the user. They must change it on first login.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !createForm.email}
          >
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialog.open} onClose={() => setRoleDialog({ open: false, userId: '', currentRole: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ open: false, userId: '', currentRole: '' })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRoleUpdate}
            disabled={updatingRole || newRole === roleDialog.currentRole}
          >
            {updatingRole ? 'Updating...' : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
